import type { Control, FieldErrors, UseFormSetValue, UseFormTrigger } from 'react-hook-form';
import type { Renter } from '@/utils/types/renter.interface';

import { Controller } from 'react-hook-form';
import dayjs from 'dayjs';

import { LeaseDatePicker, useLinkedLeaseDateRange } from '@/features/LeaseDetails';

type LeaseStartDatePickerProps = {
  control: Control<Renter, any>;
  errors: FieldErrors<Renter>;
  setValue: UseFormSetValue<Renter>;
  trigger: UseFormTrigger<Renter>;
  endDateManuallyEdited: React.MutableRefObject<boolean>;
};

export const LeaseStartDatePicker = ({
  control,
  errors,
  setValue,
  trigger,
  endDateManuallyEdited,
}: LeaseStartDatePickerProps) => {
  const error = errors.leaseStart;

  const { onStartDateChange, onStartDateError } = useLinkedLeaseDateRange({
    setStartDate: (date, options) =>
      setValue('leaseStart', date ? (date.toDate() as Date) : (null as unknown as Date), options),
    setEndDate: (date, options) =>
      setValue('leaseEnd', date ? (date.toDate() as Date) : (null as unknown as Date), options),
    triggerStartDate: () => trigger('leaseStart'),
    triggerEndDate: () => trigger('leaseEnd'),
    endDateManuallyEdited,
  });

  return (
    <Controller
      name="leaseStart"
      control={control}
      render={({ field }) => {
        const pickerValue = field.value ? dayjs(field.value) : null;

        return (
          <LeaseDatePicker
            title="Lease start date"
            value={pickerValue}
            onChange={onStartDateChange}
            onError={onStartDateError}
            error={error}
            required
            dataTestId="lease_start"
            minDate={dayjs().subtract(1, 'year')}
            maxDate={dayjs().add(1, 'year')}
            size="small"
            redAsterisk
          />
        );
      }}
    />
  );
};
